"use client";
import { CheckCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { createOrder, verifySignature } from "api/subscription/razorpay";
import { AuthenticationOptions } from "components/AuthenticationOptions";
import {
  useAppDispatch,
  useAppSelector,
  useSaveStateToLocalStorageOnChange,
  useSetInitialStore,
} from "lib/redux/hooks";
import {
  selectAccessToken,
  selectDodoRedirectUrl,
  selectIsAuthenticationOpen,
  setDodoRedirectUrl,
  setIsAuthenticationOpen,
  setOAuthOpenedFromButton,
} from "lib/redux/uiSlice";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { fetchCountryCode } from "lib/utils/getCountryCode";
import { fetchDodoProductPlans } from "lib/utils/fetchDodoProductPlans";
import { retryApiCall } from "lib/utils/retryApiCall";

declare global {
  interface Window {
    Razorpay: any;
  }
}

const PlanMoneyCard = ({
  price,
  days,
  save,
  planId,
  isSelected,
  setSelectedPlan,
  countryCode,
}: {
  price: number;
  days: string;
  save: string;
  planId: number;
  isSelected: boolean;
  countryCode: string;
  setSelectedPlan: (index: number) => void;
}) => {
  // deterministic hourly activity generator
  const getHourlyActivity = (plan: number, hour: number) => {
    
  const planMap: Record<number, number> = { 7: 1, 8: 2, 9: 3 };
  const normalizedPlan = planMap[plan] || plan;
    const ranges: Record<number, [number, number]> = {
      //specific ranges for the three primary plans
      1: [40, 50], // ₹69 -> 40-50
      2: [50, 60], // ₹99 -> 50-60
      3: [30, 40], // ₹199 -> 30-40
      // 7: [40, 50], // Outside IN 14 days 
      // 8: [50, 60], // Outside IN 30 days
      // 9: [30, 40], // Outside IN 3 months
    };
    const [min, max] = ranges[normalizedPlan] || [10, 50];
    const rangeSize = max - min;

    // Simple deterministic seed using date + normalizedPlan
    const date = new Date().toISOString().slice(0, 10);
    let seed = 0;
    for (let c of (date + String(normalizedPlan))) seed += c.charCodeAt(0);

    // Simple pseudo-random generator based on hour
    const value = (seed * (hour + 1) * 9301 + 49297) % 233280;
    const normalized = value / 233280;

    // Map to range
    return Math.floor(min + normalized * rangeSize);
  };

  const currentHour = new Date().getHours();
  const activityCount = getHourlyActivity(planId, currentHour);
  return (
    <div
      className="flex w-full cursor-pointer items-center gap-3 rounded-xl border border-[#D7DEEA] bg-white px-4 py-3"
      onClick={() => setSelectedPlan(planId)}
    >
      <input
        type="radio"
        checked={isSelected}
        onChange={() => setSelectedPlan(planId)}
        className="cursor-pointer"
      />

      <div className="text-xl font-semibold flex-1">
        <div>
          {countryCode === "IN" ? "₹" : "$"}
          {price}
        </div>
        <div className="-mt-2 text-[10px] text-[#707F8F]">For {days}</div>
      </div>

      {/* Right-aligned badges: primary save badge and activity badge */}
      <div className="ml-3 flex flex-col items-end gap-2">
        {save !== "" && (
          <div className="rounded-md bg-[#834CFF] px-2 py-[4px] text-[10px] text-white">
            {save}
          </div>
        )}
        <div className="rounded-md bg-[#4225f1] px-2 py-[4px] text-[10px] text-white">
          {activityCount} subscribed in last hour
        </div>
      </div>
    </div>
  );
};

export interface ProductPlan {
  productId: string;
  planId: number;
}

export const handlePaymentOutsideIndia = (
  productPlans: ProductPlan[],
  selectedPlan: number
) => {
  const dodoProductId = productPlans?.find(
    (productPlan) => productPlan.planId === selectedPlan
  )?.productId;
  const redirectWithSource = `${window.location.origin}/verify-dodo-payment`;
  window.location.href = `https://${
    process.env.NEXT_PUBLIC_DODO_URL
  }/buy/${dodoProductId}?quantity=1&redirect_url=${encodeURIComponent(
    redirectWithSource
  )}`;
};

export const SubscriptionPlans = () => {
  useSetInitialStore();
  useSaveStateToLocalStorageOnChange();
  const isAuthenticationOpen = useAppSelector(selectIsAuthenticationOpen);
  const accessToken = useAppSelector(selectAccessToken);
  const dispatch = useAppDispatch();
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [countryCode, setCountryCode] = useState<string | null>(null);
  const [productPlans, setProductPlans] = useState<ProductPlan[]>();
  const router = useRouter();

  useEffect(() => {
    fetchCountryCode(setCountryCode);
    fetchDodoProductPlans(setProductPlans);
  }, []);

  const handlePaymentSuccess = useCallback(async (response: any) => {
    try {
      const verificationResponse = await verifySignature(response);
      if (verificationResponse.verified) {
        window.location.href = "/resume-builder";
      } else {
        setIsProcessing(false);
        alert("Payment verification failed.");
      }
    } catch (error) {
      setIsProcessing(false);
      alert("An error occurred during payment verification.");
    }
  }, []);

  const handlePayment = async () => {
    if (!selectedPlan) {
      alert("Please select a plan before subscribing.");
      return;
    }
    try {
      setIsProcessing(true);
      // const response = await createOrder({
      //   planId: selectedPlan?.toString(),
      // });
      const response = await retryApiCall(
        () => createOrder({ planId: selectedPlan?.toString() }),
        3
      );
      const orderDetails = response.data;
      const options = {
        key_id: process.env.NEXT_PUBLIC_RAZORPAY_KEY_ID as string,
        amount: orderDetails.amount,
        currency: orderDetails.currency,
        order_id: orderDetails.id,
        name: "Resumemate",
        description: "Payment for subscription",
        image: "/assets/resumemateLogo.png",
        prefill: {
          name: "User's Name",
          email: "user@example.com",
          contact: "1234567890",
        },
        theme: {
          color: "#3399cc",
        },
        method: {
          paylater: false,
        },
        handler: handlePaymentSuccess,
        modal: {
          ondismiss: function () {
            console.log("Checkout form closed.");
            setIsProcessing(false);
            // handlePaymentDismiss();
          },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      setIsProcessing(false);
      console.error("Error...", error);
    }
  };

  const handleCancel = () => {
    router.back();
  };

  const handleSubscribe = () => {
    if (!accessToken) {
      dispatch(setIsAuthenticationOpen(true));
      dispatch(setOAuthOpenedFromButton("subscribe"));
      return;
    }
    if (selectedPlan !== null) {
      if (countryCode === "IN") {
        handlePayment();
      } else {
        dispatch(setDodoRedirectUrl("/resume-builder"));
        handlePaymentOutsideIndia(productPlans!, selectedPlan);
      }
    } else {
      alert("Please select a plan before subscribing.");
    }
  };

  if (isProcessing) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="relative h-16 w-16">
          <div className="absolute inset-0 animate-spin rounded-full border-4 border-b-blue-500 border-l-transparent border-r-transparent border-t-blue-500"></div>
          <div className="reverse-spin absolute inset-4 animate-spin rounded-full border-4 border-b-blue-500 border-l-transparent border-r-transparent border-t-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <main className="flex h-screen w-screen items-center justify-center bg-[#F1F1F1]">
      {countryCode ? (
        <>
          <SubscriptionPlansDesktop
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
            handleCancel={handleCancel}
            handleSubscribe={handleSubscribe}
            countryCode={countryCode}
          />
          <SubscriptionPlansMobile
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
            handleCancel={handleCancel}
            handleSubscribe={handleSubscribe}
            countryCode={countryCode}
          />
        </>
      ) : (
        <div className="h-16 w-16 animate-spin rounded-full border-t-4 border-solid border-gray-300 border-t-blue-500" />
      )}
      {isAuthenticationOpen && <AuthenticationOptions />}
    </main>
  );
};

interface SubscriptionPlanComponentsProps {
  selectedPlan: number | null;
  setSelectedPlan: (index: number) => void;
  handleCancel: () => void;
  handleSubscribe: () => void;
  countryCode: string;
}

export const SubscriptionPlansDesktop = ({
  selectedPlan,
  setSelectedPlan,
  handleCancel,
  handleSubscribe,
  countryCode,
}: SubscriptionPlanComponentsProps) => {
  return (
    <div className="hidden h-[90%] w-[940px] rounded-[20px] bg-white p-5 font-montserrat shadow-md md:flex">
      <div className="flex w-[60%] flex-col gap-2 pl-7 pt-7">
        <div className="flex items-center gap-1 lg:gap-2">
          <div className="relative h-[28px] w-[24px] md:h-[35px] md:w-[30px] lg:h-[42px] lg:w-[36px] 2xl:h-[51px] 2xl:w-[44px]">
            <Image
              src="/assets/resumemateLogo.png"
              alt="ResumeMate"
              fill={true}
            />
          </div>
          <div className="font-montserrat text-[10px] font-semibold leading-3 md:text-xs md:leading-3 lg:text-xs lg:leading-3 xl:text-sm xl:leading-4 2xl:text-base 2xl:leading-4">
            <span className="text-[#1C75BC]">Resume</span> <br />
            <span className="text-[#27AAE1]">Mate.io</span>
          </div>
        </div>
        <h2 className="text-xl font-bold">Upgrade to Plus</h2>
        <p className="text-xs text-[#707F8F]">
          Do more with Premium and create an AI-enhanced resume that stands out!
        </p>
        <ul>
          {deliverables.map((deliverable, index) => (
            <li className="mt-5 flex gap-5" key={deliverable.icon}>
              <Image src={deliverable.icon} alt="icon" width={23} height={23} />
              <span className="font-montserrat text-xl font-bold text-[#212121]">
                {deliverable.content}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-5 max-w-[410px] font-montserrat text-xs text-[#707F8F]">
          Stand out from the crowd and make a lasting impression! With our
          cutting-edge AI enhancements, crafting a resume that gets noticed has
          never been easier. Choose us and unlock the secret to landing your
          dream job!
        </p>
      </div>
      <div className="flex-1 rounded-2xl bg-[#F1F1F1] p-7 font-montserrat">
        <h3 className="font-bold">Starter Plan</h3>
        <ul>
          {moneyCards
            .filter((moneyCard) => {
              if (countryCode === "IN") {
                return moneyCard.countryCode === "IN";
              }
              return moneyCard.countryCode !== "IN";
            })
            .map((moneyCard, index) => (
              <li className="mt-3" key={moneyCard.planId}>
                <PlanMoneyCard
                  price={moneyCard.price}
                  days={moneyCard.days}
                  save={moneyCard.save}
                  planId={moneyCard.planId}
                  isSelected={selectedPlan === moneyCard.planId}
                  setSelectedPlan={setSelectedPlan}
                  countryCode={countryCode}
                />
              </li>
            ))}
        </ul>
        <p className="mt-3 font-semibold text-[#06004B]">No Auto Renewal</p>
        <div className="mt-3 flex gap-3 font-poppins text-[10px]">
          <button
            className="w-1/2 rounded-lg bg-white px-4 py-2"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            className="w-1/2 rounded-lg bg-[#4225F1] px-4 py-2 text-white"
            onClick={handleSubscribe}
          >
            Subscribe Now
          </button>
        </div>
      </div>
    </div>
  );
};

export const SubscriptionPlansMobile = ({
  selectedPlan,
  setSelectedPlan,
  handleCancel,
  handleSubscribe,
  countryCode,
}: SubscriptionPlanComponentsProps) => {
  return (
    <div className="relative flex w-[311px] flex-col gap-5 rounded-[14px] border border-purple-500 bg-[#2D2D2D] px-3 pb-3 pt-10 font-montserrat text-white md:hidden">
      <div
        className="absolute right-2 top-2 h-5 w-5 cursor-pointer rounded-full bg-white p-[2px]"
        onClick={handleCancel}
      >
        <XMarkIcon className=" text-black" />
      </div>

      <p className="text-[8.5px] text-[#F8E297]">Loving the Ai Experience?</p>
      <h3 className="text-xs font-semibold">Do more with Premium</h3>

      <p className="text-[8.5px]">
        Subscribe today and create a
        <strong>
          high-scoring <br /> AI-enhanced ATS-friendly
        </strong>
        , resume that stands out!
      </p>

      <ul className="flex justify-between text-[8.5px]">
        <li className="inline-flex gap-1">
          <CheckCircleIcon className="h-3 w-3 text-white" fill="#4CAF50" />
          Builder Ai
        </li>
        <li className="inline-flex gap-1">
          <CheckCircleIcon className="h-3 w-3 text-white" fill="#4CAF50" />{" "}
          Resume Reach Insights
        </li>
        <li className="inline-flex gap-1">
          <CheckCircleIcon className="h-3 w-3 text-white" fill="#4CAF50" /> Save
          Unlimited resumes
        </li>
      </ul>

      <ul className="text-black">
        {moneyCards
          .filter((moneyCard) => {
            if (countryCode === "IN") {
              return moneyCard.countryCode === "IN";
            }
            return moneyCard.countryCode !== "IN";
          })
          .map((moneyCard, index) => (
            <li className="mt-3" key={moneyCard.planId}>
              <PlanMoneyCard
                price={moneyCard.price}
                days={moneyCard.days}
                save={moneyCard.save}
                planId={moneyCard.planId}
                isSelected={selectedPlan === moneyCard.planId}
                setSelectedPlan={setSelectedPlan}
                countryCode={countryCode}
              />
            </li>
          ))}
      </ul>

      <div className="mt-3 flex gap-3 font-poppins text-[10px]">
        <button
          className="w-1/2 rounded-lg bg-white px-4 py-2 text-black"
          onClick={handleCancel}
        >
          Cancel
        </button>
        <button
          className="w-1/2 rounded-lg bg-[#4225F1] px-4 py-2 text-white"
          onClick={handleSubscribe}
        >
          Subscribe Now
        </button>
      </div>
    </div>
  );
};

const deliverables = [
  {
    icon: "/assets/payment/builderSubscriptionPlans/subsplan1.png",
    content: "Access to Builder Ai",
  },
  {
    icon: "/assets/payment/builderSubscriptionPlans/subsplan2.png",
    content: "Easily sharable link",
  },
  {
    icon: "/assets/payment/builderSubscriptionPlans/subsplan3.png",
    content: "Views & download metrics",
  },
  {
    icon: "/icons/checkScoreBlack.svg",
    content: "Check Resume Score",
  },
  {
    icon: "/assets/payment/builderSubscriptionPlans/subsplan4.png",
    content: "Save Unlimited resumes",
  },
  {
    icon: "/assets/payment/builderSubscriptionPlans/subsplan5.png",
    content: "Access to all templates",
  },
];

const moneyCards = [
  {
    countryCode: "IN",
    planId: 1,
    price: 69,
    days: "14 Days",
    save: "Save 10%",
  },
  {
    countryCode: "IN",
    planId: 2,
    price: 99,
    days: "1 Month",
    save: "Popular, Save 25%",
  },
  {
    countryCode: "IN",
    planId: 3,
    price: 199,
    days: "3 Months",
    save: "Best Value, Save 53%",
  },
  {
    countryCode: "OTHERS",
    planId: 7,
    price: 9,
    days: "14 Days",
    save: "Save 10%",
  },
  {
    countryCode: "OTHERS",
    planId: 8,
    price: 15,
    days: "1 Month",
    save: "Popular, Save 25%",
  },
  {
    countryCode: "OTHERS",
    planId: 9,
    price: 29,
    days: "3 Months",
    save: "Best Value, Save 53%",
  },
];
